import React, { useState, lazy, Suspense, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  IconButton,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  Drawer,
  Link,
  Container,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Star,
  CheckCircle,
  People,
  Speed,
  Security,
  Spa,
  LocalOffer,
  Phone,
  Email,
  LocationOn,
  Close,
  RequestQuote,
  Schedule,
  SupportAgent,
  ThumbUp,
  Verified,
  ArrowForward,
} from '@mui/icons-material';
import { motion, useInView } from 'framer-motion';
import ModernSectionBlock from '../components/ModernSectionBlock';
import KeenSliderCarousel from '../components/KeenSliderCarousel';
// Remove the import since we'll fetch the data dynamically
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import { useTenant } from '../contexts/TenantContext';
import { trackButtonClick } from '../utils/analytics';
import QuoteQuestionnaireAdapter from '../components/questionnaire/QuoteQuestionnaireAdapter';
import { QuoteProvider } from '../contexts/QuoteContext';
import { QuestionnaireProvider } from '../contexts/QuestionnaireContext';
import ServicesGrid from '../components/ServicesGrid';
import HospitalityStandardSection from '../components/HospitalityStandardSection';
import hospitalityStandards from '../data/hospitalityStandards.json';
import { useNavigate } from 'react-router-dom';
import { getBookingFlowPath } from '../utils/featureFlags';
import Seo from '../components/Seo';

// Lazy load CustomerInfoModal and HeroQuoteForm
const CustomerInfoModal = lazy(() => import('../components/CustomerInfoModal'));
const HeroQuoteForm = lazy(() => import('../components/HeroQuoteForm'));

// Pricing data for all service types
const pricingData = {
  deepClean: {
    label: 'Luxury Deep Clean',
    emoji: '🧹',
    description: 'Hotel-grade deep reset of the entire home',
    tiers: [
      { label: 'Small Home', sqft: 'Up to ~1,200 sq ft (1–2 beds, 1–2 baths)', start: '$249', range: '$249–$329' },
      { label: 'Medium Home', sqft: '1,200–2,000 sq ft (approx. 3 beds, 2 baths)', start: '$299', range: '$299–$449' },
      { label: 'Large Home', sqft: '2,000–2,800 sq ft (approx. 4 beds, 3 baths)', start: '$399', range: '$399–$599' },
      { label: 'Executive', sqft: '2,800+ sq ft luxury homes (5+ beds, 3–4+ baths)', start: '$599', range: '$599–$899' },
    ],
  },
  moveInOut: {
    label: 'Move-In / Move-Out',
    emoji: '🚚',
    description: 'Perfect for realtors, home sellers & closings',
    tiers: [
      { label: 'Small Home', sqft: 'Up to ~1,200 sq ft (1–2 beds, 1–2 baths)', start: '$299', range: '$299–$399' },
      { label: 'Medium Home', sqft: '1,200–2,000 sq ft (approx. 3 beds, 2 baths)', start: '$399', range: '$399–$549' },
      { label: 'Large Home', sqft: '2,000–2,800 sq ft (approx. 4 beds, 3 baths)', start: '$499', range: '$499–$699' },
      { label: 'Executive', sqft: '2,800+ sq ft luxury homes (5+ beds, 3–4+ baths)', start: '$699', range: '$699–$1100' },
    ],
  },
  recurring: {
    label: 'Recurring Maintenance',
    emoji: '🏡',
    description: 'Weekly, biweekly, or monthly service (Requires initial Luxury Deep Clean)',
    isRecurring: true,
    tiers: [
      { label: 'Small Home', sqft: 'Up to ~1,200 sq ft (1–2 beds, 1–2 baths)', weekly: '$119', biweekly: '$129', monthly: '$149' },
      { label: 'Medium Home', sqft: '1,200–2,000 sq ft (approx. 3 beds, 2 baths)', weekly: '$139', biweekly: '$159', monthly: '$179' },
      { label: 'Large Home', sqft: '2,000–2,800 sq ft (approx. 4 beds, 3 baths)', weekly: '$159', biweekly: '$179', monthly: '$209' },
      { label: 'Executive', sqft: '2,800+ sq ft luxury homes (5+ beds, 3–4+ baths)', weekly: '$229', biweekly: '$269', monthly: '$329' },
    ],
  },
  vacationRental: {
    label: 'Airbnb / Vacation Rental',
    emoji: '🛏️',
    description: 'Hotel-style guest readiness & staging',
    tiers: [
      { label: 'Small STR', sqft: '1–2 bedroom vacation rentals', start: '$129', range: '$129–$159' },
      { label: 'Medium STR', sqft: '2–3 bedroom vacation rentals', start: '$149', range: '$149–$189' },
      { label: 'Large STR', sqft: '3–4 bedroom vacation rentals', start: '$179', range: '$179–$239' },
      { label: 'Executive STR', sqft: 'Large 4–7 bedroom villas and premium STRs', start: '$229', range: '$229–$329' },
    ],
  },
  postConstruction: {
    label: 'Post-Construction',
    emoji: '🔧',
    description: 'Fine dust removal & multi-pass detail work',
    tiers: [
      { label: 'Small Home', sqft: 'Up to ~1,200 sq ft (1–2 beds, 1–2 baths)', start: '$349', range: '$349–$449' },
      { label: 'Medium Home', sqft: '1,200–2,000 sq ft (approx. 3 beds, 2 baths)', start: '$449', range: '$449–$599' },
      { label: 'Large Home', sqft: '2,000–2,800 sq ft (approx. 4 beds, 3 baths)', start: '$599', range: '$599–$799' },
      { label: 'Executive', sqft: '2,800+ sq ft luxury homes (5+ beds, 3–4+ baths)', start: '$799', range: '$799–$1200' },
    ],
  },
};

// Hero Pricing Section Component
const HeroPricingSection = () => {
  const [selectedService, setSelectedService] = useState('deepClean');
  const currentPricing = pricingData[selectedService];

  return (
    <Box
      sx={{
        mb: { xs: 3, sm: 4 },
        maxWidth: '950px',
        mx: 'auto',
        bgcolor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        border: '1px solid',
        borderColor: 'rgba(250, 224, 63, 0.2)',
      }}
    >
      {/* Service Type Dropdown */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2.5 } }}>
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: 220, sm: 280 },
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(250,224,63,0.1)',
              borderRadius: 2,
              '& fieldset': {
                borderColor: 'gold.main',
                borderWidth: 2,
              },
              '&:hover fieldset': {
                borderColor: 'gold.light',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'gold.main',
              },
            },
            '& .MuiSelect-select': {
              color: 'white',
              fontWeight: 600,
              py: 1.25,
            },
            '& .MuiSvgIcon-root': {
              color: 'gold.main',
            },
          }}
        >
          <Select
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'primary.main',
                  border: '1px solid',
                  borderColor: 'gold.main',
                  '& .MuiMenuItem-root': {
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(250,224,63,0.15)',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(250,224,63,0.25)',
                      '&:hover': {
                        bgcolor: 'rgba(250,224,63,0.3)',
                      },
                    },
                  },
                },
              },
            }}
          >
            {Object.entries(pricingData).map(([key, data]) => (
              <MenuItem key={key} value={key}>
                {data.emoji} {data.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography
          variant="body2"
          sx={{
            color: 'secondary.light',
            fontSize: { xs: '0.75rem', sm: '0.8rem' },
            mt: 1,
            opacity: 0.85,
          }}
        >
          {currentPricing.description}
        </Typography>
      </Box>

      {/* Pricing Tiers */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'row' },
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: { xs: 0.75, sm: 1.5 },
          flexWrap: 'wrap',
        }}
      >
        {currentPricing.tiers.map((tier) => (
          <Box
            key={tier.label}
            sx={{
              bgcolor: 'rgba(250,224,63,0.1)',
              border: '1px solid',
              borderColor: 'rgba(250,224,63,0.35)',
              borderRadius: 2,
              px: { xs: 1.25, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              textAlign: 'center',
              flex: { xs: '1 1 45%', sm: '1 1 auto' },
              minWidth: { xs: '135px', sm: '170px' },
              maxWidth: { xs: '48%', sm: '210px' },
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(250,224,63,0.18)',
                borderColor: 'gold.main',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'gold.main',
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                display: 'block',
                fontWeight: 700,
              }}
            >
              {tier.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'secondary.light',
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                display: 'block',
                opacity: 0.75,
                mb: 0.5,
              }}
            >
              {tier.sqft}
            </Typography>
            {currentPricing.isRecurring ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={{ color: 'white', fontSize: { xs: '0.65rem', sm: '0.7rem' }, lineHeight: 1.3 }}>
                  <Box component="span" sx={{ color: 'gold.main', fontWeight: 600 }}>Weekly:</Box> {tier.weekly}
                </Typography>
                <Typography sx={{ color: 'white', fontSize: { xs: '0.65rem', sm: '0.7rem' }, lineHeight: 1.3 }}>
                  <Box component="span" sx={{ color: 'gold.main', fontWeight: 600 }}>Biweekly:</Box> {tier.biweekly}
                </Typography>
                <Typography sx={{ color: 'white', fontSize: { xs: '0.65rem', sm: '0.7rem' }, lineHeight: 1.3 }}>
                  <Box component="span" sx={{ color: 'gold.main', fontWeight: 600 }}>Monthly:</Box> {tier.monthly}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  lineHeight: 1.2,
                }}
              >
                {tier.range}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Custom Scroll Animation Component
const ScrollAnimatedSection = ({
  children,
  delay = 0,
  direction = 'up',
  duration = 0.6,
  threshold = 0.1,
  once = true
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true, // CRITICAL: Always use once: true to prevent repeated animations
    margin: `-${threshold * 100}px 0px -${threshold * 100}px 0px`,
    amount: 0.3 // Only trigger when 30% visible
  });

  const getVariants = () => {
    const baseVariants = {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    };

    // Simplified variants with reduced complexity for better performance
    switch (direction) {
      case 'up':
        return {
          hidden: { ...baseVariants.hidden, y: 20 }, // Reduced from 50
          visible: { ...baseVariants.visible, y: 0 }
        };
      case 'down':
        return {
          hidden: { ...baseVariants.hidden, y: -20 }, // Reduced from -50
          visible: { ...baseVariants.visible, y: 0 }
        };
      case 'left':
        return {
          hidden: { ...baseVariants.hidden, x: 20 }, // Reduced from 50
          visible: { ...baseVariants.visible, x: 0 }
        };
      case 'right':
        return {
          hidden: { ...baseVariants.hidden, x: -20 }, // Reduced from -50
          visible: { ...baseVariants.visible, x: 0 }
        };
      case 'scale':
        return {
          hidden: { ...baseVariants.hidden, scale: 0.95 }, // Reduced from 0.8
          visible: { ...baseVariants.visible, scale: 1 }
        };
      case 'fade':
      default:
        return baseVariants; // Most performant option
    }
  };

  // Device performance detection
  const isLowEndDevice = React.useMemo(() => {
    if (typeof navigator !== 'undefined') {
      const memory = navigator.deviceMemory || 4;
      const cores = navigator.hardwareConcurrency || 4;
      return memory < 4 || cores < 4;
    }
    return false;
  }, []);

  return (
    <motion.div
      ref={ref}
      variants={getVariants()}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      transition={{
        duration: isLowEndDevice ? duration * 0.5 : duration, // Faster animations on low-end devices
        delay: 0, // Remove delay from transition for better performance
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      {children}
    </motion.div>
  );
};

// Stagger Animation for Grid Items
const StaggerContainer = ({ children, staggerDelay = 0.1, maxItems = 10 }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.1
      }
    }
  };

  // Limit number of animated items for better performance
  const limitedChildren = React.Children.toArray(children).slice(0, maxItems);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px 0px -50px 0px" }} // Changed to once: true and reduced margin
    >
      {limitedChildren}
    </motion.div>
  );
};

const StaggerItem = ({ children, delay = 0 }) => {
  const itemVariants = {
    hidden: { opacity: 0, y: 15 }, // Reduced from 30
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0 } // Remove delay for better performance
    }
  };

  return (
    <motion.div variants={itemVariants}>
      {children}
    </motion.div>
  );
};

const Landing = () => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [isQuoteMode, setIsQuoteMode] = useState(false);
  const [questionnaireDrawerOpen, setQuestionnaireDrawerOpen] = useState(false);

  // Device performance detection for animation optimization - memoized to prevent re-renders
  const isLowEndDevice = React.useMemo(() => {
    if (typeof navigator !== 'undefined') {
      const memory = navigator.deviceMemory || 4;
      const cores = navigator.hardwareConcurrency || 4;
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      return memory < 4 || cores < 4 || isMobile;
    }
    return false;
  }, []);

  // Animation level based on device performance - memoized
  const animationLevel = React.useMemo(() =>
    isLowEndDevice ? 'reduced' : 'full',
    [isLowEndDevice]
  );

  // Log performance optimization status - only once on mount
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Landing page animation level: ${animationLevel}`, {
        isLowEndDevice,
        deviceMemory: navigator.deviceMemory || 'unknown',
        hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
        userAgent: navigator.userAgent
      });
    }
  }, []); // Empty dependency array - only run once

  // Contact form state - kept for legacy support if needed elsewhere
  const [contactSuccess, setContactSuccess] = useState(false);

  // Add global CSS animations for hero text effects - optimized for performance
  React.useEffect(() => {
    // Only add animations if device can handle them
    if (isLowEndDevice) {
      return; // Skip animations on low-end devices
    }

    // Check if animations are already injected to prevent duplicates
    if (document.getElementById('landing-animations')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'landing-animations';
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.9; transform: scale(1.02); }
      }
      
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      
      @keyframes particleGlow {
        0%, 100% { opacity: 0.3; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(1.1); }
      }
      
      @keyframes gradientShift {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      
      @keyframes typing {
        from { width: 0; }
        to { width: 100%; }
      }
      
      @keyframes blink-caret {
        from, to { border-color: transparent; }
        50% { border-color: #FAE03F; }
      }
      
      @keyframes shimmer {
        0% { left: -100%; }
        100% { left: 100%; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById('landing-animations');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, [isLowEndDevice]);

  // Service catalog data is now managed by ServiceCatalogContext - removed unused fetch


  // Handler specifically for quote requests
  const handleQuoteRequest = (buttonLocation) => {
    trackButtonClick('get_quote', buttonLocation);
    setIsQuoteMode(true); // Quote mode
    setRegistrationModalOpen(true); // Show quote info modal
  };

  // Handler for questionnaire
  const handleQuestionnaireOpen = (buttonLocation) => {
    trackButtonClick('get_quote_questionnaire', buttonLocation);
    setQuestionnaireDrawerOpen(true);
  };

  const handleQuestionnaireComplete = (customerInfo, quoteInfo, rawAnswers) => {
    console.log('Quote questionnaire completed:', { customerInfo, quoteInfo, rawAnswers });
    setQuestionnaireDrawerOpen(false);

    // Show success message for quote request
    // You could redirect to a confirmation page if needed
    setContactSuccess(true);
  };

  const handleQuestionnaireClose = () => {
    setQuestionnaireDrawerOpen(false);
  };

  // Handler for contact actions
  const handleContactAction = (actionType) => {
    trackButtonClick('contact', actionType);

    if (actionType === 'call') {
      const phoneNumber = tenant?.clientSupportPhone || tenant?.contactPhone;
      if (phoneNumber) {
        window.open(`tel:${phoneNumber}`);
      } else {
        alert('Phone number not available. Please try again later.');
      }
    } else if (actionType === 'email') {
      const email = tenant?.contactEmail;
      if (email) {
        window.open(`mailto:${email}`);
      } else {
        alert('Email address not available. Please try again later.');
      }
    }
  };

  // Format phone number for display (e.g., (555) 123-4567)
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.city}, ${address.street}, ${address.state} ${address.zip}`;
  };



  // Services data for the existing services section
  const galleryData = [


    {
      type: 'video',
      src: '/media/gallery/CleanTeamOffice10sec.webm',
      title: 'Professional Office Cleaning',
      description: 'Our efficient team provides top-tier cleaning services for your commercial spaces.'
    },
    {
      type: 'marketing',
      title: 'Hotel-Level Standards for Everyday Homes',
      description: ''
    },
    {
      type: 'image',
      src: '/media/gallery/Bathroomcounterblack.webp',
      alt: 'Black Bathroom Counter',
      title: 'Sleek and Spotless Surfaces',
      description: 'We ensure all surfaces, from marble to granite, are gleaming and streak-free.'
    },
    {
      type: 'marketing',
      title: 'Guest-Ready Vacation Rentals, Every Time',
      description: ''
    },
    {
      type: 'image',
      src: '/media/gallery/Bathroombathtubwithtowelsgoldwhite.webp',
      alt: 'Bathroom with towels',
      title: 'Luxurious Bathroom Cleaning',
      description: 'Experience a spa-like freshness in your bathrooms with our meticulous cleaning.'
    },
    {
      type: 'video',
      src: '/media/gallery/CleaningProductsinCleanKitchen10sec.webm',
      title: 'Eco-Friendly Kitchen Solutions',
      description: 'Using safe and effective products to sanitize and shine your kitchen.'
    },
    {
      type: 'marketing',
      title: 'Professional Teams, Clear Processes, Predictable Results',
      description: ''
    },
    {
      type: 'image',
      src: '/media/gallery/CleaningSuppliesbranded.webp',
      alt: 'Branded Cleaning Supplies',
      title: 'Quality Supplies, Quality Clean',
      description: 'We use only the best, branded cleaning supplies for a superior and safe clean.'
    },
    {
      type: 'image',
      src: '/media/gallery/KitchenCounterWhite.webp',
      alt: 'White Kitchen Counter',
      title: 'Immaculate Kitchens',
      description: 'Grease and grime disappear, revealing a sparkling and inviting kitchen.'
    },
    {
      type: 'marketing',
      title: 'From family homes to high-turnover Airbnbs, our work is guided by checklists, quality checks, and a commitment to delivering fairytale-level results every time.',
      description: ''
    },
    {
      type: 'image',
      src: '/media/gallery/Laundryroomwhite.webp',
      alt: 'White Laundry Room',
      title: 'Organized Laundry Spaces',
      description: 'We help declutter and clean your laundry room, making chores a breeze.'
    },
    {

      type: 'video',
      src: '/media/gallery/VacationRental21sec.webm',
      title: 'Vacation Rental Turnover',
      description: 'Fast and thorough cleaning services to prepare your rental for the next guests.'
    },
    {
      type: 'marketing',
      title: 'Experienced, Vetted Teams',
      description: 'Background-checked professionals with proven track records'
    },
    {
      type: 'image',
      src: '/media/gallery/LivingRoomphoto.webp',
      alt: 'Cozy Living Room',
      title: 'Comfort and Cleanliness Combined',
      description: 'Enjoy a fresh and tidy living environment, perfect for family and guests.'
    },
    {
      type: 'video',
      src: '/media/gallery/MagicalHomeFairy10sec.webm',
      title: 'The Magic Touch',
      description: 'Witness the magical transformation of your home with our expert cleaning services.'
    },
    {
      type: 'marketing',
      title: 'Satisfaction Guarantee',
      description: 'We fix issues fast and stand behind our work'
    },
    {
      type: 'image',
      src: '/media/gallery/LivingRoomModernWhite.webp',
      alt: 'Modern White Living Room',
      title: 'Inviting Living Areas',
      description: 'Transform your living room into a pristine and welcoming space for relaxation.'
    },
    {
      type: 'marketing',
      title: 'Professional-Grade Supplies Included',
      description: 'No need to stock cleaning supplies — we bring everything'
    },
    {
      type: 'video',
      src: '/media/gallery/VacationRentalA.webm',
      title: 'Seamless Rental Preparation',
      description: 'Ensuring your vacation property is pristine and ready for every arrival.'
    },
    {
      type: 'marketing',
      title: 'Fully Licensed & Insured',
      description: 'Complete peace of mind for your property'
    },
    {
      type: 'image',
      src: '/media/gallery/conciergerie.webp',
      alt: 'Concierge Service',
      title: 'Concierge-Level Service',
      description: 'Beyond cleaning, we offer personalized services to meet your unique needs.'
    },
    {
      type: 'marketing',
      title: 'Eco-Friendly Options Available',
      description: 'Green cleaning solutions for health-conscious families'
    },
    {
      type: 'image',
      src: '/media/gallery/vacation-rental-turnover-clean.webp',
      alt: 'Vacation Rental Cleaning',
      title: 'Dedicated Vacation Rental Care',
      description: 'Specialized cleaning for vacation properties, ensuring guest satisfaction every time.'
    },
    {
      type: 'marketing',
      title: 'Professional Cleaning. Fairy-Tale Results.',
      description: ''
    }
  ];


  const commitments = [
    {
      label: 'On-Time & Reliable',
      description: 'We respect your time. Our teams arrive within the agreed window and keep you updated on every job.',
      icon: Schedule
    },
    {
      label: 'Clear Scope & Pricing',
      description: 'No vague promises. We define exactly what\'s included, with itemized pricing and no hidden fees.',
      icon: Speed
    },
    {
      label: 'Checklist-Driven Quality',
      description: 'Each service follows a structured checklist tailored to your property type and service level.',
      icon: SupportAgent
    },
    {
      label: 'Responsive Support',
      description: 'Questions, changes, or special requests? Our coordination team is here to help before and after each visit.',
      icon: Verified
    }
  ];

  const features = [
    {
      icon: Security,
      title: 'Trusted & Vetted Teams',
      description: 'Our cleaners are carefully selected, trained, and supported with clear processes so you can feel confident about who is in your home.',
      image: '/media/features/feature_1.webp'
    },
    {
      icon: Spa,
      title: 'Professional Products & Methods',
      description: 'We use proven products and techniques suited to your surfaces and property type, with options to accommodate specific requests.',
      image: '/media/features/feature_2.webp'
    },
    {
      icon: Speed,
      title: 'Fast, Efficient & Organized',
      description: 'We plan each visit around a defined scope and checklist so the work is efficient, thorough, and consistent from one clean to the next.',
      image: '/media/features/feature_3.webp'
    },
    {
      icon: LocalOffer,
      title: 'A Coordination Team at Your Service',
      description: 'Behind every cleaning team is a coordination desk that helps with scheduling, special instructions, and follow-up when you need it.',
      image: '/media/features/feature_4.webp'
    }
  ];

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      maxWidth: '100vw',
      overflowX: 'hidden'
    }}>
      {/* Miami Service Area Announcement Banner */}
      <Box sx={{
        bgcolor: 'primary.main',
        py: 1.5,
        px: 2,
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        position: 'relative',
        zIndex: 10
      }}>
        <Typography
          variant="body1"
          sx={{
            color: 'gold.main',
            fontWeight: 600,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }}
        >
          🌴 Now Serving Select Miami Neighborhoods! 
          <Typography
            component="span"
            sx={{
              color: 'white',
              fontWeight: 400,
              ml: 1,
              fontSize: { xs: '0.85rem', sm: '0.95rem' }
            }}
          >
            Fisher Island, Brickell, Coral Gables & More
          </Typography>
        </Typography>
      </Box>
      
      {/* TopBar */}
      <TopBar />
      <Seo
        title="House Cleaning Services in Orlando & Central Florida"
        description="Book trusted house cleaning in Orlando, Kissimmee & Davenport. Deep cleaning, recurring maintenance, move-in/out and hotel-standard vacation-rental turnovers by vetted professionals."
        path="/"
      />

      {/* Hero Section */}
      <ScrollAnimatedSection direction="fade" delay={0.1}>
        <ModernSectionBlock
          variant="hero"
          backgroundVideo="/images/hero.webm"
          videoFallbackBg="primary.main"
          enableMobileVideo={true}
          overlay={true}
          overlayOpacity={0.3}
          titleSx={{
            fontSize: { xs: '3rem', sm: '4rem', md: '5.5rem', lg: '6.5rem', xl: '7.5rem' },
            fontWeight: 800,
            lineHeight: theme.typography.h1.lineHeight || 1.1
          }}
          subtitleSx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.2rem', lg: '3.8rem', xl: '4.2rem' },
            fontWeight: theme.typography.h2.fontWeight,
            lineHeight: theme.typography.h2.lineHeight || 1.2
          }}
          descriptionSx={{
            fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem', lg: '2.6rem', xl: '3rem' },
            fontWeight: 500,
            lineHeight: theme.typography.subtitle1.lineHeight || 1.4
          }}
          showBadge={true}
          showActionButton={false}
          animation="fade"
          animationDelay={0.2}
        >


          {/* Custom Hero Text Overlay - Multiple Effect Options */}
          <Box sx={{
            position: 'relative',
            zIndex: 10,
            textAlign: 'center',
            width: { xs: '95%', sm: '85%', md: '70%', lg: '60%' },
            px: { xs: 1.5, sm: 3, md: 4 },
            py: { xs: 0, sm: 4, md: 6 },
            mx: 'auto'
          }}>

            {/* Main H1 Tagline */}
            <Typography
              variant="h1"
              sx={{
                color: 'white',
                textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 0 30px rgba(0,0,0,0.4)',
                mb: { xs: 1, sm: 2 },
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                fontWeight: 700,
                lineHeight: 1.2
              }}
            >
              Hotel-Grade Cleaning{' '}
              <Box component="span" sx={{ display: { xs: 'block', md: 'inline' } }}>
                for Luxury Homes Across Florida
              </Box>
            </Typography>

            {/* Pitch Line */}
            <Typography
              variant="h2"
              sx={{
                color: 'gold.main',
                textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 0 30px rgba(0,0,0,0.4)',
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1.1rem', sm: '1.4rem', md: '1.6rem' },
                fontWeight: 600,
                fontStyle: 'italic'
              }}
            >
              Where Five-Star Standards Meet Your Front Door
            </Typography>

            {/* Subheading */}
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 0 30px rgba(0,0,0,0.4)',
                mb: { xs: 2, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.3rem' },
                fontWeight: 400,
                lineHeight: 1.5,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              A higher standard for homes that expect more — luxury cleaning & concierge service with hotel-grade detailing, photo reporting, and white-glove professionalism.
            </Typography>

            {/* Pricing Transparency with Dropdown */}
            <HeroPricingSection />

            {/* CTA Buttons - MOVED TO TOP FOR ALL DEVICES */}

            {/* Mobile Buttons - Full Width */}
            <Box sx={{
              display: { xs: 'flex', sm: 'none' },
              flexDirection: 'column',
              gap: 2.5,
              width: '100%',
              px: 0,
              mb: 3,
              mt: 0
            }}>
              <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    trackButtonClick('book_now', 'hero_section');
                    navigate(getBookingFlowPath());
                  }}
                  startIcon={<Schedule sx={{ fontSize: 24 }} />}
                  sx={{
                    bgcolor: '#4CAF50',
                    color: 'white',
                    fontWeight: 800,
                    px: 4,
                    py: 2.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.125rem',
                    letterSpacing: '0.3px',
                    height: '70px',
                    width: '100%',
                    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.25)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      bgcolor: '#45a049',
                      transform: 'translateY(-3px) scale(1.02)',
                      boxShadow: '0 6px 20px 0 rgba(0,0,0,0.35)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Book Now
                </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleQuestionnaireOpen('hero_section')}
                startIcon={<RequestQuote sx={{ fontSize: 24 }} />}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'gold.main',
                  fontWeight: 800,
                  px: 4,
                  py: 2.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.125rem',
                  letterSpacing: '0.3px',
                  height: '70px',
                  width: '100%',
                  boxShadow: '0 4px 14px 0 rgba(0,0,0,0.25)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 6px 20px 0 rgba(0,0,0,0.35)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Get My Custom Cleaning Estimate
              </Button>
              <Button
                variant="contained"
                size="large"
                component="a"
                href={`tel:${tenant?.clientSupportPhone || tenant?.contactPhone || ''}`}
                startIcon={<Phone sx={{ fontSize: 45 }} />}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'gold.main',
                  fontWeight: 800,
                  px: 4,
                  py: 2.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  letterSpacing: '0.3px',
                  height: '70px',
                  width: '100%',
                  display: 'inline-flex',
                  textDecoration: 'none',
                  boxShadow: '0 4px 14px 0 rgba(0,0,0,0.25)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 6px 20px 0 rgba(0,0,0,0.35)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Call Our Cleaning Coordinator
              </Button>
            </Box>

            {/* Desktop Buttons - No Background Container */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                flexDirection: 'column',
                gap: 2.5,
                alignItems: 'center',
                mb: 3
              }}
            >
              <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    trackButtonClick('book_now', 'hero_section');
                    navigate(getBookingFlowPath());
                  }}
                  startIcon={<Schedule sx={{ fontSize: 28 }} />}
                  sx={{
                    bgcolor: '#4CAF50',
                    color: 'white',
                    fontWeight: 800,
                    px: 6,
                    py: 2.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.25rem',
                    letterSpacing: '0.5px',
                    width: '450px',
                    height: '80px',
                    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.25)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      bgcolor: '#45a049',
                      transform: 'translateY(-3px) scale(1.02)',
                      boxShadow: '0 6px 20px 0 rgba(0,0,0,0.35)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Book Now
                </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleQuestionnaireOpen('hero_section')}
                startIcon={<RequestQuote sx={{ fontSize: 24 }} />}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'gold.main',
                  fontWeight: 800,
                  px: 6,
                  py: 2.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.25rem',
                  letterSpacing: '0.5px',
                  width: '450px',
                  height: '80px',
                  boxShadow: '0 4px 14px 0 rgba(0,0,0,0.25)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 6px 20px 0 rgba(0,0,0,0.35)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Get My Custom Cleaning Estimate
              </Button>
              <Button
                variant="contained"
                size="large"
                component="a"
                href={`tel:${tenant?.clientSupportPhone || tenant?.contactPhone || ''}`}
                startIcon={<Phone sx={{ fontSize: 48 }} />}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'gold.main',
                  fontWeight: 800,
                  px: 6,
                  py: 2.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  letterSpacing: '0.5px',
                  width: '450px',
                  height: '80px',
                  display: 'inline-flex',
                  textDecoration: 'none',
                  boxShadow: '0 4px 14px 0 rgba(0,0,0,0.25)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 6px 20px 0 rgba(0,0,0,0.35)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Call Our Cleaning Coordinator
              </Button>
            </Box>

            {/* Service Area Link */}
            <Box
              sx={{
                textAlign: 'center',
                mb: 3,
                mt: 1
              }}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  const serviceAreaSection = document.querySelector('#service-area-main');
                  if (serviceAreaSection) {
                    serviceAreaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                  trackButtonClick('service_area_link', 'hero_cta');
                }}
                sx={{
                  color: 'gold.main',
                  textDecoration: 'underline',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  '&:hover': {
                    color: 'white',
                    textDecoration: 'none',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                View Our Service Areas →
              </Link>
            </Box>

            {/* Option 1: Gradient Text with Glow Effect */}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                mb: { xs: 2, sm: 3 },
                px: { xs: 1, sm: 2 }
              }}
            >
              {[
                { label: 'Deep Clean (First-Time Service)', hash: '#services' },
                { label: 'Recurring Maintenance', hash: '#services' },
                { label: 'Move-In / Move-Out', hash: '#services' },
                { label: 'Vacation-Rental Turnovers', hash: '#services' },
                { label: 'Post-Construction', hash: '#services' }
              ].map((service, index, array) => (
                <React.Fragment key={service.label}>
                  <Link
                    href={service.hash}
                    onClick={(e) => {
                      e.preventDefault();
                      const servicesSection = document.querySelector('#services');
                      if (servicesSection) {
                        servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                      trackButtonClick('service_link', service.label);
                    }}
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                      textShadow: (theme) => `0 0 20px ${theme.palette.primary.dark}99`,
                      textDecoration: 'none',
                      fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem', lg: '1.6rem', xl: '1.8rem' },
                      fontWeight: 800,
                      lineHeight: 1.4,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: 'gold.main',
                        textShadow: (theme) => `0 0 30px ${theme.palette.gold.main}99`,
                        transform: 'scale(1.05)',
                      }
                    }}
                  >
                    {service.label}
                  </Link>
                  {index < array.length - 1 && (
                    <Typography
                      component="span"
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem', lg: '1.6rem', xl: '1.8rem' },
                        fontWeight: 800,
                        textShadow: (theme) => `0 0 20px ${theme.palette.primary.dark}99`,
                      }}
                    >
                      •
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>


            {/* Badge Row - Horizontal Layout for Better UX */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'row' },
                gap: { xs: 1, md: 2 },
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >

              {/* Pricing Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  bgcolor: 'gold.main',
                  color: 'gold.contrastText',
                  px: { xs: 1.5, sm: 2.5, md: 2 },
                  py: { xs: 1.5, sm: 1.2, md: 1 },
                  borderRadius: 0,
                  boxShadow: 0,
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  },
                  width: { xs: '160px', sm: '220px', md: '240px' },
                  height: { xs: 'auto', sm: '70px', md: '80px' },
                  minHeight: { xs: '90px', sm: '70px', md: '80px' },
                  flex: { xs: 'none', md: '1' }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '1rem', md: '1.1rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 1,
                      position: 'relative',
                      textAlign: 'center',
                      lineHeight: { xs: 1.1, sm: 1.2, md: 1.2 }
                    }}
                  >
                    Checklist-Driven Quality
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.9rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 1,
                      position: 'relative',
                      textAlign: 'center',
                      lineHeight: { xs: 1.1, sm: 1.2, md: 1.2 },
                      color: 'primary.main',
                      mt: 0.5
                    }}
                  >
                    Every clean follows a detailed professional checklist.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 20,
                    height: 20,
                    bgcolor: 'accent.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: 'white',
                    boxShadow: 2,
                    zIndex: 2
                  }}
                >
                  !
                </Box>
              </Box>


              {/* Service Availability Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  bgcolor: 'gold.main',
                  color: 'gold.contrastText',
                  px: { xs: 1.5, sm: 2.5, md: 2 },
                  py: { xs: 1.5, sm: 1.2, md: 1 },
                  borderRadius: 0,
                  boxShadow: 0,
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  },
                  width: { xs: '160px', sm: '220px', md: '240px' },
                  height: { xs: 'auto', sm: '70px', md: '80px' },
                  minHeight: { xs: '90px', sm: '70px', md: '80px' },
                  flex: { xs: 'none', md: '1' }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '1rem', md: '1.1rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 1,
                      position: 'relative',
                      textAlign: 'center',
                      lineHeight: { xs: 1.1, sm: 1.2, md: 1.2 }
                    }}
                  >
                    Trusted for Homes & Rentals
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.9rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 1,
                      position: 'relative',
                      textAlign: 'center',
                      lineHeight: { xs: 1.1, sm: 1.2, md: 1.2 },
                      color: 'primary.main',
                      mt: 0.5
                    }}
                  >
                    Ideal for families, investors, and Airbnb hosts.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 20,
                    height: 20,
                    bgcolor: 'accent.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: 'white',
                    boxShadow: 2,
                    zIndex: 2
                  }}
                >
                  ⚡
                </Box>
              </Box>

              {/* Satisfaction Guarantee Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  bgcolor: 'gold.main',
                  color: 'gold.contrastText',
                  px: { xs: 1.5, sm: 2.5, md: 2 },
                  py: { xs: 1.5, sm: 1.2, md: 1 },
                  borderRadius: 0,
                  boxShadow: 0,
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  },
                  width: { xs: '160px', sm: '220px', md: '240px' },
                  height: { xs: 'auto', sm: '70px', md: '80px' },
                  minHeight: { xs: '90px', sm: '70px', md: '80px' },
                  flex: { xs: 'none', md: '1' }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '1rem', md: '1.1rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 1,
                      position: 'relative',
                      textAlign: 'center',
                      lineHeight: { xs: 1.1, sm: 1.2, md: 1.2 }
                    }}
                  >
                    Satisfaction Focused
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.9rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 1,
                      position: 'relative',
                      textAlign: 'center',
                      lineHeight: { xs: 1.1, sm: 1.2, md: 1.2 },
                      color: 'primary.main',
                      mt: 0.5
                    }}
                  >
                    We fix issues fast and stand behind our work.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 20,
                    height: 20,
                    bgcolor: 'accent.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: 'white',
                    boxShadow: 2,
                    zIndex: 2
                  }}
                >
                  ✓
                </Box>
              </Box>
            </Box>






          </Box>
        </ModernSectionBlock>
      </ScrollAnimatedSection>

      {/* Luxury Cleaning Process Section */}
      <ScrollAnimatedSection direction="up" delay={0.15}>
        <Box
          component="section"
          className="luxury-process"
          sx={{
            bgcolor: 'primary.main',
            py: { xs: 6, sm: 8, md: 10 },
            px: { xs: 2, sm: 4, md: 6 },
          }}
        >
          <Container maxWidth="md" className="luxury-process-container">
            {/* Title */}
            <Typography
              variant="h2"
              component="h2"
              className="luxury-process-title"
              sx={{
                color: 'gold.main',
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                mb: { xs: 3, md: 4 },
                textAlign: 'center',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Our Luxury Cleaning Process
            </Typography>

            {/* Intro Paragraph */}
            <Typography
              variant="body1"
              component="p"
              className="luxury-process-intro"
              sx={{
                color: 'secondary.main',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                lineHeight: 1.8,
                textAlign: 'center',
                mb: { xs: 4, md: 5 },
                opacity: 0.95,
              }}
            >
              We don't just "do a cleaning." Every visit follows our written{' '}
              <Box
                component="strong"
                sx={{
                  color: 'gold.main',
                  fontWeight: 600,
                }}
              >
                Luxury Cleaning Process Charter
              </Box>
              {' '}– a hotel-grade checklist that guarantees the same high standard every time,
              no matter which team comes to your home.
            </Typography>

            {/* Process List */}
            <Box
              component="ul"
              className="luxury-process-list"
              sx={{
                listStyle: 'none',
                p: 0,
                m: 0,
                mb: { xs: 4, md: 5 },
              }}
            >
              {[
                {
                  title: 'Hygienic & Safe Methods',
                  description: 'Fresh cloths and mop heads for each home, color-coded for kitchen/bathroom, HEPA vacuums, and no cross-contamination.',
                },
                {
                  title: 'Whole-Home Dust & Air Reset',
                  description: 'Top-to-bottom dust removal on ceilings, vents, fixtures, furniture, and baseboards before we touch the floors.',
                },
                {
                  title: 'Material-Aware Detailing',
                  description: 'Surface-specific products and techniques for stone, wood, glass, stainless steel, and luxury finishes – no harsh "one-size-fits-all" chemicals.',
                },
                {
                  title: 'Hotel-Style Finishing Touches',
                  description: 'Edges, corners, switches, handles, beds, towels, and floors finished to a "walk-in-ready" look, with optional photo report.',
                },
              ].map((item, index) => (
                <Box
                  component="li"
                  key={item.title}
                  className="luxury-process-list-item"
                  sx={{
                    position: 'relative',
                    pl: { xs: 4, sm: 5 },
                    py: { xs: 1.5, sm: 2 },
                    color: 'secondary.main',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                    lineHeight: 1.7,
                    borderBottom: index < 3 ? '1px solid' : 'none',
                    borderColor: 'rgba(250,224,63,0.15)',
                    '&::before': {
                      content: '"✓"',
                      position: 'absolute',
                      left: 0,
                      top: { xs: '1.5rem', sm: '2rem' },
                      transform: 'translateY(-50%)',
                      color: 'gold.main',
                      fontWeight: 700,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    },
                  }}
                >
                  <Box
                    component="strong"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                    }}
                  >
                    {item.title}
                  </Box>
                  {' '}–{' '}
                  <Box
                    component="span"
                    sx={{
                      opacity: 0.9,
                    }}
                  >
                    {item.description}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* CTA Link */}
            <Box
              className="luxury-process-link"
              sx={{
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body1"
                component="p"
                sx={{
                  color: 'secondary.main',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  mb: 2,
                }}
              >
                Want to see exactly how we clean?
              </Typography>
              <Link
                href="/luxury-cleaning-charter"
                underline="none"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'gold.main',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  pb: 0.5,
                  borderBottom: '2px solid',
                  borderColor: 'gold.main',
                  '&:hover': {
                    color: 'gold.light',
                    borderColor: 'gold.light',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                Read our full Luxury Cleaning Process Charter
                <ArrowForward sx={{ fontSize: '1.1rem' }} />
              </Link>
            </Box>
          </Container>
        </Box>
      </ScrollAnimatedSection>

      {/* Commitments Section */}
      <ScrollAnimatedSection direction="up" delay={0.2}>
        <ModernSectionBlock
          variant="stats"
          bgcolor="white"
          color="primary.main"
          title="Going the Extra Mile for You"
          titleColor="primary.main"
          subtitle="Our commitment to earning your trust and business"
          animation="slide"
          animationDelay={0.3}
          py={{ xs: 4, sm: 6, md: 8 }}
        >
          <StaggerContainer staggerDelay={0.1}>
            <Grid container spacing={{ xs: 3, sm: 4, md: 5 }} sx={{ mt: 2, width: '100%', maxWidth: '100%' }}>
              {commitments.map((commitment, index) => (
                <Grid item xs={12} sm={6} md={3} key={commitment.label}>
                  <StaggerItem delay={index * 0.1}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        color: 'primary.main',
                        p: { xs: 2, sm: 3 },
                        minHeight: { xs: 180, sm: 200, md: 220 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 3,
                          bgcolor: 'white'
                        }
                      }}
                    >
                      <Box
                        component={commitment.icon}
                        sx={{
                          fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                          color: 'primary.main',
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: 'primary.main',
                          mb: 1.5,
                          fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem' },
                        }}
                      >
                        {commitment.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 400,
                          color: 'text.secondary',
                          fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                          textAlign: 'center',
                          lineHeight: 1.6,
                        }}
                      >
                        {commitment.description}
                      </Typography>
                    </Box>
                  </StaggerItem>
                </Grid>
              ))}
            </Grid>
          </StaggerContainer>
        </ModernSectionBlock>
      </ScrollAnimatedSection>

      {/* Our Services Section - New Modern Cards */}
      <ScrollAnimatedSection direction="up" delay={0.2}>
        <Box
          id="services"
          sx={{
            bgcolor: 'grey.50',
            py: { xs: 4, md: 6 },
            width: '100%'
          }}
        >
          <ServicesGrid />
        </Box>
      </ScrollAnimatedSection>

      {/* New Service Promo — A Man With A Truck (junk removal & cleanouts) */}
      <ScrollAnimatedSection direction="up" delay={0.2}>
        <ModernSectionBlock
          variant="feature"
          bgcolor="#111111"
          title="New Service — A Man With A Truck"
          titleColor="gold.main"
          subtitle="Junk removal & cleanout services — fast, reliable & professional. We haul. You relax."
          subtitleColor="grey.300"
          align="center"
          py={{ xs: 4, sm: 6, md: 8 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 3, md: 6 },
              mt: 3
            }}
          >
            <Box
              component="a"
              href="/media/services/a-man-with-a-truck.webp"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'block', lineHeight: 0 }}
              aria-label="View the full A Man With A Truck flyer"
            >
              <Box
                component="img"
                src="/media/services/a-man-with-a-truck-web.webp"
                alt="A Man With A Truck — junk removal and cleanout services flyer. We remove furniture, appliances, construction debris and more. Serving North Port, Venice, Port Charlotte, Englewood, Sarasota and surrounding areas. Minimum pickup $100. Call or text 863-350-6951."
                loading="lazy"
                sx={{
                  width: '100%',
                  maxWidth: { xs: 340, sm: 420, md: 460 },
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'scale(1.02)' }
                }}
              />
            </Box>

            <Box sx={{ maxWidth: 420, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h6" sx={{ color: 'grey.100', fontWeight: 700, mb: 1.5 }}>
                Need junk gone without the hassle?
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.400', mb: 1 }}>
                Furniture, appliances, garage and estate cleanouts, construction
                debris, storm cleanup — single items or full properties.
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.500', mb: 3 }}>
                Licensed &amp; insured · Same-day availability · Upfront pricing
                with free estimates · Serving North Port, Venice, Port Charlotte,
                Englewood, Sarasota &amp; surrounding areas.
              </Typography>
              <Button
                variant="contained"
                size="large"
                href="tel:8633506951"
                onClick={() => trackButtonClick('man_with_truck_call', 'landing_promo')}
                sx={{
                  bgcolor: 'gold.main',
                  color: '#111',
                  fontWeight: 700,
                  px: 4,
                  '&:hover': { bgcolor: 'gold.dark' }
                }}
              >
                Call or Text 863-350-6951
              </Button>
            </Box>
          </Box>
        </ModernSectionBlock>
      </ScrollAnimatedSection>

      {/* Short-Term Rental Turnover Standard — hospitality technique */}
      {/*
        Deliberately NOT wrapped in ScrollAnimatedSection: that helper gates on
        amount: 0.3, so 30% of the wrapped node must be visible at once. Stacked
        on mobile this section is ~2800px tall, which exceeds any phone viewport
        and leaves it stuck at opacity 0. HospitalityStandardSection animates each
        row on its own, so the wrapper was redundant as well as harmful.
      */}
      <ModernSectionBlock
        variant="default"
        bgcolor="background.paper"
        title={hospitalityStandards.title}
        subtitle={hospitalityStandards.subtitle}
        align="center"
        py={{ xs: 6, sm: 8, md: 10 }}
      >
        <HospitalityStandardSection
          cta={{
            label: 'See Our Turnover Standard',
            href: '/vacation-rentals',
            onClick: () => trackButtonClick('hospitality_standard_cta', 'landing'),
          }}
        />
      </ModernSectionBlock>

      {/* New Service Promo — Hospitality Linen Program (short-term rentals) */}
      <ScrollAnimatedSection direction="up" delay={0.2}>
        <ModernSectionBlock
          variant="feature"
          bgcolor="grey.50"
          title="Hospitality Linen Program"
          subtitle="Hotel-quality linens for your vacation rental, professionally managed for $59/month — no upfront linen investment, wear replacement included."
          align="center"
          py={{ xs: 4, sm: 6, md: 8 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              textAlign: 'center'
            }}
          >
            <Box
              component="img"
              src="/media/services/linen_fivestar_t300_solidstack.webp"
              alt="Folded stack of white T-300 cotton-rich hotel bed sheets from the Hospitality Linen Program"
              loading="lazy"
              decoding="async"
              sx={{
                width: '100%',
                maxWidth: { xs: 280, sm: 340, md: 380 },
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                display: 'block',
                borderRadius: 3,
                boxShadow: 3
              }}
            />
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
              Three complete linen rotations, quality inspections, inventory management and
              replacement due to normal wear — fully integrated with your scheduled turnover
              cleaning. See what owning your own linens really costs, and what you save.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="/hospitality-linen"
              onClick={() => trackButtonClick('hospitality_linen_promo', 'landing_promo')}
              sx={{ px: 4, fontWeight: 700 }}
            >
              Compare the Costs
            </Button>
          </Box>
        </ModernSectionBlock>
      </ScrollAnimatedSection>

      {/* OLD Our Services Section - COMMENTED OUT
      <ScrollAnimatedSection direction="up" delay={0.2}>
        <Box sx={{
          width: '100%'
        }}>

          <Box sx={{
            width: '100%',
            bgcolor: 'grey.50',
            borderRadius: 3,
            overflow: 'hidden'
          }}>
            <Box sx={{ p: { xs: 2, md: 4 }, textAlign: 'center' }}>
              <Typography
                variant="h1"
                sx={{
                  color: 'text.primary',
                  mb: 3
                }}
              >
                What We Offer
              </Typography>
            </Box>
            <OurServices
              services={ourServicesData}
              sectionTitle={!isMobile ? "Comprehensive cleaning solutions" : undefined}
              setRegistrationModalOpen={setRegistrationModalOpen}
            />
          </Box>
        </Box>
      </ScrollAnimatedSection>
      */}

      {/* Gallery Section */}
      <ScrollAnimatedSection direction="scale" delay={0.3}>
        <Box sx={{
          width: '100%',
          minHeight: { xs: 300, sm: 400, md: 500, lg: 600 },
          bgcolor: 'primary.main',
          borderRadius: 0,
          overflow: 'hidden',
          py: { xs: 2, md: 4 }
        }}>
          <KeenSliderCarousel mediaItems={galleryData} />
        </Box>
      </ScrollAnimatedSection>


      {/* Review, Tagline, and Trust Partner Grid Section */}
      <Box sx={{
        width: '100%',
        py: { xs: 4, md: 6, lg: 8 },
        px: { xs: 2, sm: 3, md: 4 },
        bgcolor: 'primary.main',
        minHeight: { xs: 200, md: 'auto' }, // Ensure minimum height on mobile
        position: 'relative',
        zIndex: 1,
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        opacity: 1, // Ensure it's always visible
        animation: isLowEndDevice ? 'none' : 'fadeIn 1s ease-in-out',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(20px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}>
        <Grid
          container
          spacing={{ xs: 1, sm: 2, md: 4 }}
          sx={{
            width: '100%',
            maxWidth: '100%',
            margin: 0,
            '& .MuiGrid-item': {
              paddingLeft: { xs: '4px', sm: '8px', md: '16px' },
              paddingTop: { xs: '4px', sm: '8px', md: '16px' }
            }
          }}
        >
          {/* Review Section */}
          <Grid item xs={12} sm={12} md={4} sx={{ width: '100%', maxWidth: '100%' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              p: { xs: 1, sm: 2, md: 3 },
              minHeight: { xs: 'auto', md: '100%' },
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: 'gold.main'
                }}
              >
                What Our Clients Say
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: 'gold.main',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontStyle: 'italic',
                  textAlign: 'center',
                  lineHeight: 1.4
                }}
              >
                Homeowners, investors, and property managers trust us with their properties and their reputation.
              </Typography>

              <Box sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: { xs: 1.5, sm: 2, md: 2.5 },
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'divider',
                width: '100%',
                maxWidth: '100%',
                mx: 'auto',
                boxSizing: 'border-box'
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: 'gold.main',
                    fontSize: { xs: '1.1rem', sm: '1.2rem' }
                  }}
                >
                  ⭐️⭐️⭐️⭐️⭐️
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontStyle: 'italic',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' }
                  }}
                >
                  "Absolutely blown away by the level of service from 5 Fairies Cleaning! I booked them for a deep clean before family came into town, and they went above and beyond — every surface sparkled, and even the baseboards and vents were spotless. Communication was clear, they showed up right on time, and the team was super friendly. It honestly felt like walking into a brand-new home. I'll definitely be booking them again!"
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: theme.typography.subtitle2.fontWeight,
                    color: 'text.primary',
                  }}
                >
                  – Jessica R., Orlando, FL
                </Typography>
              </Box>

              {/* Second Customer Review */}
              <Box sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: { xs: 2, sm: 3, md: 3, lg: 2.5 },
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'divider',
                width: '100%',
                maxWidth: '100%',
                mx: 'auto',
                boxSizing: 'border-box',
                mt: { xs: 2, sm: 3 }
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: 'gold.main',
                    fontSize: { xs: '1.1rem', sm: '1.2rem' }
                  }}
                >
                  ⭐️⭐️⭐️⭐️⭐️
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontStyle: 'italic',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' }
                  }}
                >
                  "As a vacation rental host, I've tried a few cleaning services — but 5 Fairies Cleaning is on another level. They handled everything: linens, towels, restocking supplies, even light staging to match my listing photos. Guests have been raving about how clean and welcoming the space feels. Their team is reliable, communicative, and genuinely cares about quality. I finally feel like I can relax between bookings!"
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: theme.typography.subtitle2.fontWeight,
                    color: 'text.primary',
                  }}
                >
                  – Michael T., Kissimmee, FL
                </Typography>

              </Box>
              <Box sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: { xs: 2, sm: 3, md: 3, lg: 2.5 },
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'divider',
                width: '100%',
                maxWidth: '100%',
                mx: 'auto',
                boxSizing: 'border-box',
                mt: { xs: 2, sm: 3 }
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: 'gold.main',
                    fontSize: { xs: '1.1rem', sm: '1.2rem' }
                  }}
                >
                  ⭐️⭐️⭐️⭐️⭐️
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    fontStyle: 'italic',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' }
                  }}
                >
                  "   I have to say im rather impressed with the services provided! Questioned if I was gonna be satisfied with the work done with the pricing but after giving it a shot these fine folk are now my go to, haven't encountered a company yet that pays attention to the details like crown molding or base boards like these folk. 10/10 will be hiring again"
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: theme.typography.subtitle2.fontWeight,
                    color: 'text.primary',
                  }}
                >
                  – Trenton D., Winter Garden, FL
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Tagline Section */}
          <Grid item xs={12} sm={12} md={4} sx={{ width: '100%', maxWidth: '100%' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              p: { xs: 1, sm: 2, md: 3 },
              minHeight: { xs: 'auto', md: '100%' },
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              {/* Main Tagline */}
              <Typography
                variant="h2"
                sx={{
                  color: 'gold.main',
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem', lg: '2.5rem' },
                  lineHeight: 1.3,
                  mb: { xs: 2, md: 3 }
                }}
              >
                Professional Cleaning. Fairytale Results.
              </Typography>

              {/* Body Text */}
              <Typography
                variant="body1"
                sx={{
                  color: 'secondary.light',
                  fontWeight: 400,
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem', lg: '1.2rem' },
                  lineHeight: 1.6,
                  textAlign: 'center',
                  maxWidth: '90%',
                  mx: 'auto',
                  opacity: 0.95,
                }}
              >
                We don't just "do a quick clean." We deliver a structured, professional service with clear scope, consistent standards, and a focus on peace of mind for every client.
              </Typography>
            </Box>
          </Grid>

          {/* Trust Partner Section */}
          <Grid item xs={12} sm={12} md={4} sx={{ width: '100%', maxWidth: '100%' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 1, sm: 2, md: 3 },
              minHeight: { xs: 'auto', md: '100%' },
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>

              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  color: 'gold.main',
                  fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem', lg: '2rem' },
                  lineHeight: 1.3,
                  textAlign: 'center'
                }}
              >
                Your Trusted Cleaning Partner
              </Typography>
              <Box sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                {/* Header Section */}
                <Box sx={{
                  p: { xs: 2, sm: 3, md: 3 },
                  textAlign: 'center',
                  bgcolor: (theme) => `${theme.palette.secondary.light}CC`,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Box
                    component="img"
                    src="/images/logo2.jpg"
                    alt="5 Fairies Cleaning Logo"
                    sx={{
                      width: { xs: 80, sm: 100, md: 120, lg: 120 },
                      height: 'auto',
                      mb: { xs: 1, sm: 2 },
                      maxWidth: '100%'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.log('Logo failed to load');
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'primary.main',
                      mb: 1
                    }}
                  >
                    A Cleaning Partner You Can Trust
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary'
                    }}
                  >
                    Professional, insured cleaning services for Central Florida
                  </Typography>
                </Box>

                {/* Content Section */}
                <Box sx={{ p: { xs: 2, sm: 3, md: 3, lg: 4 } }}>
                  <Box
                    component="img"
                    src="/images/persona_maid1.webp"
                    alt="Professional Cleaners"
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      mb: { xs: 1, sm: 2 },
                      objectFit: 'cover',
                      maxWidth: '100%'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.log('Maid image failed to load');
                    }}
                  />

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.primary',
                      mb: 2
                    }}
                  >
                    <strong>5 Fairies Cleaning</strong> is a professional, insured service provider, trusted by homeowners, investors, and property managers across Central Florida for reliable, high-standard cleaning and turnovers.
                  </Typography>

                  <Box component="ul" sx={{
                    pl: 2,
                    mb: 2,
                    '& li': {
                      color: 'text.primary',
                      lineHeight: theme.typography.body1.lineHeight,
                      mb: 0.5
                    }
                  }}>
                    <li>✔️ Background-checked, trained professionals</li>
                    <li>✔️ Transparent, itemized pricing</li>
                    <li>✔️ Checklist-driven quality standards</li>
                    <li>✔️ Specialized in homes and vacation rentals</li>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.primary',
                      mb: 3
                    }}
                  >
                    From deep cleans to vacation rental turnovers, we deliver professional cleaning with fairytale results.
                  </Typography>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleQuoteRequest('5fairies_partnership')}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        px: { xs: 2, md: 3, lg: 3 },
                        py: { xs: 1, md: 1.5, lg: 1.5 },
                        borderRadius: 3,
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      Get a Fast Quote
                    </Button>
                  </Box>
                </Box>

                <Box sx={{
                  bgcolor: (theme) => `${theme.palette.secondary.light}CC`,
                  p: { xs: 1.5, sm: 2, md: 2 },
                  textAlign: 'center',
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block'
                    }}
                  >
                    Recommended by <strong>Chamber of Commerce</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Questions? Reach out to us anytime!
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Features Section */}
      <ScrollAnimatedSection direction="up" delay={0.3}>
        <ModernSectionBlock
          variant="feature"
          bgcolor='primary.main'
          title="What Makes Us Different"
          titleColor="gold.main"
          subtitle="Premium cleaning services with a personal touch"
          subtitleColor="gold.main"
          description="We combine professional expertise with eco-friendly practices to deliver exceptional results every time."
          descriptionColor="gold.main"
          showDivider={true}
          animation="grow"
          animationDelay={0.2}
          py={0}
        >
          <StaggerContainer staggerDelay={0.1}>
            <Grid container spacing={4} sx={{ mt: 4, width: '100%', maxWidth: '100%' }}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={6} key={feature.title}>
                  <StaggerItem delay={index * 0.1}>
                    <Card
                      sx={{
                        height: '100%',
                        minHeight: { xs: 300, sm: 350, md: 400 },
                        p: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        overflow: 'hidden',
                        '&:hover': {
                          boxShadow: theme.shadows[8],
                          borderColor: 'gold.main',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {/* Image Section */}
                      <Box
                        sx={{
                          width: { xs: '100%', md: '50%' },
                          height: { xs: 200, md: '100%' },
                          position: 'relative',
                          order: { xs: 1, md: index % 2 === 0 ? 1 : 2 }
                        }}
                      >
                        <Box
                          component="img"
                          src={feature.image}
                          alt={feature.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center',
                            backgroundColor: 'grey.100'
                          }}
                        />
                        {/* Icon overlay on image */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <feature.icon
                            sx={{
                              fontSize: 32,
                              color: 'primary.main',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Text Section */}
                      <Box
                        sx={{
                          width: { xs: '100%', md: '50%' },
                          p: 3,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          order: { xs: 2, md: index % 2 === 0 ? 2 : 1 }
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            color: 'primary.main',
                            textAlign: { xs: 'center', md: 'left' }
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                            textAlign: { xs: 'center', md: 'left' }
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Card>
                  </StaggerItem>
                </Grid>
              ))}
            </Grid>
          </StaggerContainer>
        </ModernSectionBlock>
      </ScrollAnimatedSection>

      {/* Service Areas Section */}
      <ScrollAnimatedSection direction="up" delay={0.3}>
        <Box 
          id="service-area-main"
          sx={{
            bgcolor: 'grey.50',
            py: { xs: 6, md: 8 },
            width: '100%'
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h3"
              component="h2"
              align="center"
              sx={{
                mb: 4,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Our Service Areas Across Florida
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/service-areas')}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}
              >
                Browse cleaning services by city →
              </Button>
            </Box>

            <Grid container spacing={4} alignItems="center">
              {/* Service Areas Map */}
              <Grid item xs={12} md={8}>
                <Box sx={{ 
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: 4,
                  bgcolor: 'white'
                }}>
                  <Box
                    component="img"
                    src="/images/ourserviceareas.jpg"
                    alt="PrimeShine Service Areas Map - Florida cleaning service coverage from Orlando to Miami"
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: { xs: 300, sm: 400, md: 500 },
                      objectFit: 'contain',
                      backgroundColor: 'grey.100'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.log('Service areas map failed to load');
                    }}
                  />
                </Box>
              </Grid>

              {/* Service Areas Text */}
              <Grid item xs={12} md={4}>
                <Box sx={{ pl: { md: 2 } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      color: 'primary.main'
                    }}
                  >
                    We're Growing Across Florida
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      color: 'text.secondary',
                      lineHeight: 1.6
                    }}
                  >
                    From Central Florida to South Florida, we're expanding our premium cleaning services to serve more communities across the state.
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" color="primary.main" sx={{ mb: 1, fontWeight: 600 }}>
                      🌴 New Miami Areas
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Fisher Island, Brickell, Coral Gables, Key Biscayne, and more exclusive Miami neighborhoods.
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" color="primary.main" sx={{ mb: 1, fontWeight: 600 }}>
                      🏠 Central Florida
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Orlando metro, Tampa Bay area, and surrounding communities.
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="h6" color="primary.main" sx={{ mb: 1, fontWeight: 600 }}>
                      🏖️ Southwest Florida
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Naples, Fort Myers, Cape Coral, and coastal communities.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </ScrollAnimatedSection>

      {/* Contact Section */}
      <ScrollAnimatedSection direction="fade" delay={0.4}>
        <ModernSectionBlock
          variant="feature"
          title="Talk to Our Team"
          titleColor="gold.main"
          subtitle="Have questions about services, scope, or scheduling? Reach out and we'll help you choose the right option for your home or rental."
          subtitleColor="gold.main"
          align="center"
          animation="fade"
          animationDelay={0.3}
          py={0}
          backgroundVideo="/media/gallery/CleaningProductsinCleanKitchen10sec.webm"
          overlay={true}
          overlayOpacity={1}
        >
          <Grid container spacing={{ xs: 2, sm: 2, md: 3 }} sx={{ mt: 4, width: '100%', maxWidth: '100%' }}>
            <Grid item xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2, md: 3 },
                    cursor: 'pointer',
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.3s ease',
                    minHeight: { xs: 160, sm: 180, md: 200 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      bgcolor: (theme) => `${theme.palette.gold.main}1A`,
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      transition: 'all 0.1s ease'
                    }
                  }}
                  onClick={() => handleContactAction('call')}
                >
                  <Phone sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: 'gold.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ 
                    fontWeight: theme.typography.h5.fontWeight, 
                    mb: 1,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                  }}>
                    Call Us
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    wordBreak: 'break-word',
                    mb: 0.5
                  }}>
                    {formatPhoneDisplay(tenant?.clientSupportPhone || tenant?.contactPhone) || 'Contact us for phone number'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}>
                    Speak directly with our cleaning coordinator.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2, md: 3 },
                    cursor: 'pointer',
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.3s ease',
                    minHeight: { xs: 160, sm: 180, md: 200 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      bgcolor: (theme) => `${theme.palette.gold.main}1A`,
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      transition: 'all 0.1s ease'
                    }
                  }}
                  onClick={() => handleContactAction('email')}
                >
                  <Email sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: 'gold.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ 
                    fontWeight: theme.typography.h5.fontWeight, 
                    mb: 1,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                  }}>
                    Email Us
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    wordBreak: 'break-word',
                    mb: 0.5
                  }}>
                    {tenant?.contactEmail || 'Contact us for email address'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}>
                    Send details about your property and what you need.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={12} md={4} id="service-area">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2, md: 3 },
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.3s ease',
                    minHeight: { xs: 160, sm: 180, md: 200 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      bgcolor: (theme) => `${theme.palette.gold.main}1A`,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <LocationOn sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: 'gold.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ 
                    fontWeight: theme.typography.h5.fontWeight, 
                    mb: 1,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                  }}>
                    Service Area
                  </Typography>
                  {tenant?.address && Array.isArray(tenant.address) ? (
                    tenant.address.map((addr, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1, 
                        mb: 1.5,
                        justifyContent: 'center'
                      }}>
                        <LocationOn sx={{ 
                          fontSize: { xs: 14, sm: 16 }, 
                          color: 'gold.main',
                          mt: 0.2,
                          flexShrink: 0
                        }} />
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          wordBreak: 'break-word',
                          lineHeight: 1.4,
                          textAlign: 'left'
                        }}>
                          {formatAddress(addr)}
                        </Typography>
                      </Box>
                    ))
                  ) : tenant?.address ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 1,
                      justifyContent: 'center'
                    }}>
                      <LocationOn sx={{ 
                        fontSize: { xs: 14, sm: 16 }, 
                        color: 'gold.main',
                        mt: 0.2,
                        flexShrink: 0
                      }} />
                      <Typography variant="body2" color="text.secondary" sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        textAlign: 'left'
                      }}>
                        {formatAddress(tenant.address)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}>
                      Contact us for location information
                    </Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      fontWeight: 600,
                      mb: 1
                    }}>
                      Service Areas:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      lineHeight: 1.5
                    }}>
                      • Oviedo (Seminole County)<br/>
                      • Windermere (Orange County)<br/>
                      • Orlando - Baldwin Park (Orange County)<br/>
                      • Orlando - Lake Nona (Orange County)<br/>
                      • Orlando - College Park (Orange County)<br/>
                      • Orlando - Dr. Phillips (Orange County)<br/>
                      • Apopka (Orange County)<br/>
                      • Yalaha (Lake County)<br/>
                      • Montverde (Lake County)<br/>
                      • Tampa - South Tampa (Hillsborough County)<br/>
                      • Tampa - Westchase (Hillsborough County)<br/>
                      • Tampa - Davis Islands (Hillsborough County)<br/>
                      • Tampa - New Tampa (Hillsborough County)<br/>
                      • Lutz (Hillsborough County)<br/>
                      • Oldsmar (Pinellas County)<br/>
                      • Odessa (Pasco County)<br/>
                      • Terra Ceia (Manatee County)<br/>
                      • Longboat Key (Manatee/Sarasota Counties)<br/>
                      • Naples - Pelican Bay (Collier County)<br/>
                      • Naples - Old Naples (Collier County)<br/>
                      • Naples - Coquina Sands (Collier County)<br/>
                      • Naples - Fifth Avenue North (Collier County)<br/>
                      • Naples - North Naples (Collier County)<br/>
                      • Naples - Rural Collier (Collier County)<br/>
                      • Naples - Golden Gate Estates (Collier County)<br/>
                      • Naples - Vanderbilt Beach (Collier County)<br/>
                      • Naples - East Naples (Collier County)<br/>
                      • Marco Island (Collier County)<br/>
                      • Fort Myers - Gateway (Lee County)<br/>
                      • Fort Myers - Page Field (Lee County)<br/>
                      • Fort Myers - Tice (Lee County)<br/>
                      • Fort Myers - Villas (Lee County)<br/>
                      • Fort Myers - Whiskey Creek (Lee County)<br/>
                      • Fort Myers - Buckingham (Lee County)<br/>
                      • Cape Coral - Hancock (Lee County)<br/>
                      • Miami - Fisher Island (Miami-Dade County)<br/>
                      • Key Biscayne (Miami-Dade County)<br/>
                      • Pinecrest/Corals Gables (Miami-Dade County)<br/>
                      • Pinecrest/Kendall (Miami-Dade County)<br/>
                      • Miami - Brickell (Miami-Dade County)<br/>
                      • Miami Beach - North End (Miami-Dade County)<br/>
                      • Miami - Downtown (Miami-Dade County)<br/>
                      • Miami - Cutler Bay (Miami-Dade County)<br/>
                      • Coral Gables/South Miami (Miami-Dade County)<br/>
                      • Sarasota - Lakewood Ranch (Sarasota County)<br/>
                      • Sarasota - Downtown (Sarasota County)<br/>
                      • Sarasota - Palmer Ranch (Sarasota County)<br/>
                      • Sarasota - Siesta Key (Sarasota County)<br/>
                      • Sarasota - Fruitville (Sarasota County)<br/>
                      • Sarasota - Gulf Gate (Sarasota County)<br/>
                      • Osprey (Sarasota County)<br/>
                      • Plus surrounding areas
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </ModernSectionBlock>
      </ScrollAnimatedSection>

      {/* Quick Contact Form Section */}
      <ScrollAnimatedSection direction="fade" delay={0.5}>
        <ModernSectionBlock
          variant="feature"
          title="Get Your Custom Cleaning Estimate"
          titleColor="gold.main"
          subtitle="Tell us about your home or rental and we'll recommend the right service and schedule. No pressure, no hidden fees — just clear options and professional results."
          subtitleColor="gold.main"
          bgcolor="primary.main"
          align="center"
          py={{ xs: 4, sm: 6, md: 8 }}
        >
          <Box sx={{ 
            maxWidth: 600, 
            mx: 'auto',
            mt: 3
          }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Suspense fallback={<Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}>
                <HeroQuoteForm />
              </Suspense>
            </motion.div>
          </Box>
        </ModernSectionBlock>
      </ScrollAnimatedSection>

      {/* Success Notification */}
      <Snackbar
        open={contactSuccess}
        autoHideDuration={6000}
        onClose={() => setContactSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setContactSuccess(false)}
          severity="success"
          sx={{
            width: '100%',
            fontSize: '1rem',
            '& .MuiAlert-message': {
              fontWeight: theme.typography.h6.fontWeight
            }
          }}
        >
          🎉 Request sent! We'll call you back within 1 hour.
        </Alert>
      </Snackbar>

      {/* Guest Info Modal for HCP flow */}
      <Suspense fallback={null}>
        <CustomerInfoModal
          open={registrationModalOpen}
          onClose={() => {
            setRegistrationModalOpen(false);
            setIsQuoteMode(false); // Reset quote mode when closing modal
          }}
          isHCPFlow={false}
          isQuoteMode={isQuoteMode}
        />
      </Suspense>

      {/* Questionnaire Drawer */}
      <Drawer
        anchor="right"
        open={questionnaireDrawerOpen}
        onClose={handleQuestionnaireClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
            maxWidth: 800,
            bgcolor: 'background.default'
          }
        }}
      >
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}>
          {/* Close button */}
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.default',
            borderBottom: 1,
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Get Your Quote
            </Typography>
            <IconButton
              onClick={handleQuestionnaireClose}
              sx={{
                bgcolor: 'action.hover',
                color: 'primary.light',
                '&:hover': {
                  bgcolor: 'action.selected',
                  color: 'primary.light'
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Questionnaire content */}
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            p: 2
          }}>
            <QuoteProvider>
              <QuestionnaireProvider>
                <QuoteQuestionnaireAdapter
                  configPath="/data/customer_engagement_questionnaire.json"
                  onComplete={handleQuestionnaireComplete}
                  onError={(error) => console.error('Quote questionnaire error:', error)}
                  showProgress={true}
                  allowNavigation={true}
                />
              </QuestionnaireProvider>
            </QuoteProvider>
          </Box>
        </Box>
      </Drawer>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Landing;